import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Button, Alert, Form, Row, Col } from 'react-bootstrap';
import { CreditCard, AlertTriangle, CheckCircle, Calendar, Wallet, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as api from '../../../../services/api/expensesApi';
import { verifikatTableCss } from './verifikatTableStyles';

/**
 * BulkSettleModal
 *
 * One verifikat for many expenses. Used when the admin makes a single
 * clump-sum bank transfer covering N separate employee expenses — posting
 * N individual settlement verifikat would make the bank-statement match
 * impossible since the actual bank only sees the total.
 *
 * Verifikat shape:
 *   Debit  2891   Σ gross
 *   Credit <pay>  Σ gross
 *   + N verification_references rows (one per expense_id)
 *
 * Only selected booked expenses without payroll_id can be bulk-settled.
 */
const fmtSek = (n) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(Number(n) || 0);

const BulkSettleModal = ({ companyId, expenses, show, onClose, onSettled }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [paymentAccount, setPaymentAccount] = useState('1930');
  const [description, setDescription] = useState('');
  const [accountByNumber, setAccountByNumber] = useState({});

  const expenseIds = (expenses || []).map((e) => e.expense_id);
  const total = (expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const currencies = [...new Set((expenses || []).map((e) => e.currency || 'SEK'))];
  const mixedCurrency = currencies.length > 1;
  const nameFor = (num) => accountByNumber[String(num)]?.account_name || '';

  useEffect(() => {
    if (!show) {
      setDate(format(new Date(), 'yyyy-MM-dd'));
      setPaymentAccount('1930');
      setDescription('');
      setError(null);
      return;
    }
    setDescription(`Utbetalning utlägg: ${expenses?.length || 0} st (${fmtSek(total)})`);
    (async () => {
      try {
        const res = await axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true });
        const byNum = {};
        for (const a of (res.data?.accounts || [])) byNum[String(a.account_number)] = a;
        setAccountByNumber(byNum);
      } catch { /* silent — account-name lookup is best-effort */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleSettle = async () => {
    if (!paymentAccount.trim()) { setError('Välj betalkonto.'); return; }
    if (expenseIds.length === 0) { setError('Inga utlägg valda.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await api.settleExpensesBulk(companyId, {
        expenseIds,
        date,
        paymentAccount: paymentAccount.trim(),
        description: description && description.trim() ? description.trim() : undefined,
      });
      toast.success(`${expenseIds.length} utlägg betalda — verifikat ${res.data?.series || 'V'}${res.data?.verificationNumber}.`);
      onSettled?.(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Bulk-utbetalning misslyckades.');
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={busy ? undefined : onClose} size="lg" centered backdrop={busy ? 'static' : true}>
      <Modal.Header closeButton={!busy}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CreditCard size={18} /> Betala {expenseIds.length} utlägg samlat
          </div>
          <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#64748b' }}>
            <span style={{ fontWeight: 600, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
              {fmtSek(total)}
            </span>
            {currencies.length === 1 && <span style={{ marginLeft: 4 }}>{currencies[0]}</span>}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            <AlertTriangle size={14} className="me-1" /> {error}
          </Alert>
        )}
        {mixedCurrency && (
          <Alert variant="warning" className="mb-3">
            <AlertTriangle size={14} className="me-1" /> Utlägg i olika valutor kan inte betalas samlat. Avmarkera icke-SEK.
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-medium">
                <Calendar size={16} className="me-2" /> Utbetalningsdatum
              </Form.Label>
              <Form.Control
                type="date" value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={busy}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-medium">
                <Wallet size={16} className="me-2" /> Betalkonto (BAS-nr)
              </Form.Label>
              <Form.Control
                type="text" value={paymentAccount}
                onChange={(e) => setPaymentAccount(e.target.value)}
                placeholder="1930"
                disabled={busy}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-medium">
                <FileText size={16} className="me-2" /> Beskrivning
              </Form.Label>
              <Form.Control
                type="text" value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={255}
                disabled={busy}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* List of expenses being settled */}
        <div className="bk-compact-table">
          <div className="bk-compact-table-header d-flex">
            <div style={{ flex: 2 }}>Anställd</div>
            <div style={{ flex: 3 }}>Utlägg</div>
            <div style={{ flex: 1, textAlign: 'right' }}>Belopp</div>
          </div>
          {(expenses || []).map((e) => (
            <div key={e.expense_id} className="bk-compact-table-row d-flex">
              <div style={{ flex: 2, fontSize: '0.8125rem' }}>
                {e.employee_firstname} {e.employee_lastname || ''}
              </div>
              <div style={{ flex: 3, fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.description}>
                {e.description}
              </div>
              <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell">
                {fmtSek(e.amount)}
              </div>
            </div>
          ))}
          <div className="bk-compact-table-footer d-flex">
            <div style={{ flex: 5 }}>Summa ({expenseIds.length} st)</div>
            <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell">
              {fmtSek(total)}
            </div>
          </div>
        </div>

        {/* Verifikat preview */}
        <div className="bk-compact-table mt-3">
          <div className="bk-compact-table-header d-flex">
            <div style={{ flex: 3 }}>Konto</div>
            <div style={{ flex: 1, textAlign: 'right' }}>Debet (SEK)</div>
            <div style={{ flex: 1, textAlign: 'right' }}>Kredit (SEK)</div>
          </div>
          <div className="bk-compact-table-row d-flex align-items-center">
            <div style={{ flex: 3 }}>
              <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
                <span style={{ fontFamily: "'SF Mono', Monaco, monospace" }}>2891</span>
                {nameFor('2891') && (
                  <span style={{ color: '#64748b' }}> — {nameFor('2891')}</span>
                )}
              </div>
            </div>
            <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell">
              <span className="fw-medium text-success">{fmtSek(total)}</span>
            </div>
            <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell">
              <span className="text-muted">—</span>
            </div>
          </div>
          <div className="bk-compact-table-row d-flex align-items-center">
            <div style={{ flex: 3 }}>
              <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
                <span style={{ fontFamily: "'SF Mono', Monaco, monospace" }}>{paymentAccount || '—'}</span>
                {nameFor(paymentAccount) && (
                  <span style={{ color: '#64748b' }}> — {nameFor(paymentAccount)}</span>
                )}
              </div>
            </div>
            <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell">
              <span className="text-muted">—</span>
            </div>
            <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell">
              <span className="fw-medium text-primary">{fmtSek(total)}</span>
            </div>
          </div>
          <div className="bk-compact-table-footer d-flex">
            <div style={{ flex: 3 }}>Summa</div>
            <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell text-success">{fmtSek(total)}</div>
            <div style={{ flex: 1, textAlign: 'right' }} className="bk-amount-cell text-primary">{fmtSek(total)}</div>
          </div>
        </div>

        <div className="bk-balance-indicator bk-balance-good mt-3">
          <CheckCircle size={16} /> Debet och Kredit balanserar
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={busy}>Avbryt</Button>
        <Button variant="primary" onClick={handleSettle} disabled={busy || mixedCurrency || expenseIds.length === 0 || !paymentAccount.trim()}>
          {busy ? <Spinner size="sm" animation="border" /> : <><CheckCircle size={14} className="me-1" /> Bokför utbetalning</>}
        </Button>
      </Modal.Footer>

      <style>{verifikatTableCss}</style>
    </Modal>
  );
};

export default BulkSettleModal;
